import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Printer, Search, Mail } from "lucide-react";
import { PageHero, DraftNotice } from "@/components/common/SectionHeading";
import { api } from "@/lib/lnlApi";

export default function Requirements() {
    const [items, setItems] = useState([]);
    const [type, setType] = useState("college-freshman");
    const [q, setQ] = useState("");
    useEffect(() => { api.get("/requirements").then((r) => setItems(r.data)); }, []);
    const selected = items.find((i) => i.applicant_type === type);
    const filtered = useMemo(() => (selected?.items || []).filter((x) => x.toLowerCase().includes(q.toLowerCase())), [selected, q]);

    return (
        <div data-testid="requirements-page">
            <PageHero
                eyebrow="Requirements"
                title="Complete admission requirements"
                description="Search requirements by applicant type. Requirements may be updated — please confirm with the Admissions Office before submission."
                breadcrumbs={[{ label: "Home", to: "/" }, { label: "Admissions", to: "/admissions" }, { label: "Requirements" }]}
            />
            <section className="py-10 bg-white">
                <div className="lnl-container grid grid-cols-1 lg:grid-cols-4 gap-8">
                    <aside>
                        <div className="lnl-eyebrow mb-2">Applicant type</div>
                        <div className="flex flex-col gap-1">
                            {items.map((it) => (
                                <button key={it.applicant_type} onClick={() => setType(it.applicant_type)} className={`text-left px-3 py-2 rounded-md text-sm font-heading font-semibold ${type === it.applicant_type ? "bg-lnl-green-bg text-lnl-green-deep" : "text-lnl-charcoal hover:bg-lnl-off-white"}`} data-testid={`req-type-${it.applicant_type}`}>
                                    {it.title.replace(" Requirements", "")}
                                </button>
                            ))}
                        </div>
                    </aside>
                    <div className="lg:col-span-3">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
                            <h2 className="font-heading text-2xl font-bold text-lnl-charcoal">{selected?.title}</h2>
                            <div className="flex gap-2">
                                <button onClick={() => window.print()} className="lnl-btn-secondary" data-testid="req-print"><Printer className="h-4 w-4" /> Print</button>
                                <a href={`mailto:?subject=${encodeURIComponent(selected?.title || "LNL Requirements")}&body=${encodeURIComponent((selected?.items || []).join("\n"))}`} className="lnl-btn-secondary" data-testid="req-email"><Mail className="h-4 w-4" /> Email</a>
                            </div>
                        </div>
                        <div className="mt-4"><DraftNotice tone="warn">Admission requirements may be updated. Contact the LNL Admissions Office to confirm the latest documents before submission.</DraftNotice></div>
                        <div className="mt-4 relative">
                            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-lnl-gray" />
                            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search this checklist" className="w-full pl-10 pr-4 py-2.5 rounded-md border border-lnl-border" data-testid="req-search" />
                        </div>
                        <ul className="mt-4 space-y-2">
                            {filtered.map((it, i) => (
                                <li key={i} className="flex items-start gap-3 lnl-card p-3" data-testid={`req-item-${i}`}>
                                    <CheckCircle2 className="h-5 w-5 text-lnl-green mt-0.5 shrink-0" />
                                    <div className="text-sm text-lnl-charcoal">{it}</div>
                                </li>
                            ))}
                        </ul>
                        {selected?.notice && <div className="mt-4"><DraftNotice tone="warn">{selected.notice}</DraftNotice></div>}
                        <div className="mt-6"><Link to={`/admissions/apply?type=${type}`} className="lnl-btn-primary" data-testid="req-continue-to-apply">Continue to application</Link></div>
                    </div>
                </div>
            </section>
        </div>
    );
}
